import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import {
  AfterViewInit,
  Component,
  HostBinding,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { RouterModule, RouterOutlet } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { FeatureFlagService } from '../../services/feature-flag.service';

@Component({
  selector: 'app-admin',
  standalone: true,
  imports: [
    RouterModule,
    RouterOutlet,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatSidenavModule,
  ],
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss'],
})
export class AdminComponent implements OnInit, AfterViewInit, OnDestroy {
  private featureFlagService = inject(FeatureFlagService);
  private breakpointObserver = inject(BreakpointObserver);
  private authService = inject(AuthService);

  readonly sidenav = viewChild.required<MatSidenav>('sidenav');
  @HostBinding('class.expanded') get isExpanded() {
    return !this.isSidebarCollapsed;
  }

  private destroy$ = new Subject<void>();
  isFeatureFlagManagementEnabled = false; // Default to disabled until API confirms
  isPracticeAreaEnabled = false; // Default to disabled until API confirms
  isRoleManagementEnabled = false; // Default to disabled until API confirms
  isAuditHistoryEnabled = false; // Default to disabled until API confirms
  isSystemLogsEnabled = false; // Default to disabled until API confirms
  isLoadingFeatureFlags = true; // Show loading state until flags are loaded
  isSidebarCollapsed = true; // Always start collapsed

  // User permissions
  canViewUsersManagement = false;
  canViewPracticeAreasManagement = false;
  canViewFeatureFlagManagement = false;
  canViewAppSettingsManagement = false;
  canViewPermissionsManagement = false;
  canViewLogs = false;
  canViewAuditHistory = false;
  canViewUserFeedback = false;

  // Sidenav properties
  sidenavMode: 'side' | 'over' = 'side';
  sidenavOpened = true;
  isMobile = false;

  // Scroll detection cleanup
  private scrollCleanupFunctions: (() => void)[] = [];

  ngOnInit(): void {
    this.setupRoleSubscription();
    this.loadAllFeatureFlags();
    this.setupResponsiveBehavior();
  }

  private setupRoleSubscription(): void {
    // Subscribe to role changes to update permissions
    this.authService.userRoles$
      .pipe(takeUntil(this.destroy$))
      .subscribe((roles) => {
        this.checkUserPermissions();
      });

    // Also check immediately with current roles
    this.checkUserPermissions();
  }

  private checkUserPermissions(): void {
    // Check user permissions for each admin section
    this.canViewUsersManagement = this.authService.canViewUsersManagement();
    this.canViewPracticeAreasManagement =
      this.authService.canViewPracticeAreasManagement();
    this.canViewFeatureFlagManagement =
      this.authService.canViewFeatureFlagManagement();
    this.canViewAppSettingsManagement =
      this.authService.canViewAppSettingsManagement();
    this.canViewPermissionsManagement =
      this.authService.canViewPermissionsManagement();
    this.canViewLogs = this.authService.canViewLogs();
    this.canViewAuditHistory = this.authService.canViewAuditHistory();
    this.canViewUserFeedback = this.authService.canViewUserFeedback();
  }

  ngAfterViewInit(): void {
    // CSS now handles the margins directly, no need for manual updates
    this.setupScrollbarDetection();
  }

  // Method to manually refresh feature flags (can be called from child components)
  refreshFeatureFlags(): void {
    this.loadAllFeatureFlags();
  }

  private loadAllFeatureFlags(): void {
    this.isLoadingFeatureFlags = true;

    // Load all required feature flags in a single bulk request
    const requiredFlags = [
      'feature-flag-management',
      'practice-area-management',
      'role-management',
      'audit-logs',
      'application-logs',
    ];

    this.featureFlagService
      .loadFeatureFlags(requiredFlags)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (flagMap) => {
          this.isFeatureFlagManagementEnabled =
            flagMap.get('feature-flag-management') || false;
          this.isPracticeAreaEnabled =
            flagMap.get('practice-area-management') || false;
          this.isRoleManagementEnabled =
            flagMap.get('role-management') || false;
          this.isAuditHistoryEnabled = flagMap.get('audit-logs') || false;
          this.isSystemLogsEnabled = flagMap.get('application-logs') || false;
          this.isLoadingFeatureFlags = false;
        },
        error: (error) => {
          console.error('Error loading feature flags:', error);
          this.isFeatureFlagManagementEnabled = false;
          this.isPracticeAreaEnabled = false;
          this.isLoadingFeatureFlags = false;
        },
      });
  }

  setupResponsiveBehavior(): void {
    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isMobile = result.matches;
        if (this.isMobile) {
          this.sidenavMode = 'over';
          this.sidenavOpened = false;
          this.isSidebarCollapsed = false; // Don't use collapsed state on mobile
        } else {
          this.sidenavMode = 'side';
          this.sidenavOpened = true; // Always keep sidenav open on desktop
          // Collapsed state is handled by CSS width changes
        }
      });
  }

  toggleSidebar(): void {
    if (this.isMobile) {
      this.sidenavOpened = !this.sidenavOpened;
    } else {
      // On desktop, just toggle the collapsed state - CSS handles the rest
      this.isSidebarCollapsed = !this.isSidebarCollapsed;
    }
  }

  onNavItemClick(): void {
    // Close sidenav on mobile when navigation item is clicked
    if (this.isMobile) {
      this.sidenavOpened = false;
    }
  }

  private setupScrollbarDetection(): void {
    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(() => {
      const sidenavListContainer = document.querySelector(
        '.sidenav-list-container',
      );

      if (sidenavListContainer) {
        let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

        const handleScroll = () => {
          // Add scrolling class immediately when scrolling starts
          sidenavListContainer.classList.add('scrolling');

          // Clear existing timeout
          if (scrollTimeout) {
            clearTimeout(scrollTimeout);
          }

          // Remove scrolling class after scrolling stops (300ms delay)
          scrollTimeout = setTimeout(() => {
            sidenavListContainer.classList.remove('scrolling');
          }, 300);
        };

        sidenavListContainer.addEventListener('scroll', handleScroll);
        sidenavListContainer.addEventListener('wheel', handleScroll);

        // Store cleanup functions
        this.scrollCleanupFunctions.push(() => {
          sidenavListContainer.removeEventListener('scroll', handleScroll);
          sidenavListContainer.removeEventListener('wheel', handleScroll);
        });
      } else {
        console.error('Sidenav list container not found for scroll detection');
      }
    }, 100);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    // Toggle sidebar with Ctrl+B (Windows/Linux) or Cmd+B (Mac)
    if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
      event.preventDefault();
      this.toggleSidebar();
    }
  }

  ngOnDestroy(): void {
    // Clean up scroll event listeners
    this.scrollCleanupFunctions.forEach((cleanup) => cleanup());

    this.destroy$.next();
    this.destroy$.complete();
  }
}
